import React, { useEffect, useState } from 'react';
import { Preloader } from './Preloader';
import { useAppPreloader } from '@/shared/hooks/useAppPreloader';

interface PreloaderWrapperProps {
  children: React.ReactNode;
}

export const PreloaderWrapper: React.FC<PreloaderWrapperProps> = ({
  children,
}) => {
  const { progress, status, isComplete, error, startPreloading } =
    useAppPreloader();
  const [showApp, setShowApp] = useState(false);

  // Start preloading on mount
  useEffect(() => {
    startPreloading();
  }, [startPreloading]);

  // Handle preloader completion
  const handlePreloaderComplete = () => {
    setShowApp(true);
  };

  // Show app immediately when preloader is complete
  useEffect(() => {
    if (isComplete) {
      setShowApp(true);
    }
  }, [isComplete]);

  // Fallback: If preloader is complete but app isn't showing after 100ms, force show app
  useEffect(() => {
    if (isComplete && !showApp) {
      const fallbackTimer = setTimeout(() => {
        if (!showApp) {
          console.log('Forcing app show after preloader completion');
          setShowApp(true);
        }
      }, 100); // Very fast fallback

      return () => clearTimeout(fallbackTimer);
    }
  }, [isComplete, showApp]);

  // Handle errors - show app anyway after a short delay
  useEffect(() => {
    if (error) {
      console.error('Preloader error:', error);
      // Show app after 1 second even if there's an error
      const timer = setTimeout(() => {
        console.log('Showing app despite preloader error');
        setShowApp(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Ultimate fallback: Always show app after 10 seconds no matter what
  useEffect(() => {
    const ultimateFallback = setTimeout(() => {
      if (!showApp) {
        console.warn('Ultimate fallback: Forcing app show after 10 seconds');
        setShowApp(true);
      }
    }, 10000);

    return () => clearTimeout(ultimateFallback);
  }, [showApp]);

  // Show error state in preloader
  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="mb-4 text-4xl font-bold text-gray-900 md:text-6xl">
            Krasota
          </h1>
          <div className="mb-6 text-red-600">
            <p className="text-lg">Ошибка загрузки</p>
            <p className="text-sm text-gray-600">{error}</p>
          </div>
          <div className="text-sm text-gray-500">
            Приложение загрузится автоматически через несколько секунд...
          </div>
          {/* Loading dots */}
          <div className="mt-4 flex justify-center space-x-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="fade-in h-2 w-2 animate-pulse rounded-full bg-gray-400"
                style={{
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: '1.5s',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {!showApp && (
        <Preloader
          progress={progress}
          status={status}
          isComplete={isComplete}
          onComplete={handlePreloaderComplete}
        />
      )}

      {showApp && <>{children}</>}
    </>
  );
};
